import React, { useEffect } from "react";
import {
  Dimensions,
  Modal,
  Text,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  ScrollView,
} from "react-native";
import { View } from "../../../components/shared/Themed";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../../components/shared/Spinner";
import { getAllChinaTrackerDetailsWithShipments } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useToast } from "react-native-toast-notifications";
import { formatLocalDate } from "../../../utils/common";

interface ChinaShipmentsListModalProps {
  visible: boolean;
  onClose: () => void;
  order: any;
}

const ChinaShipmentsListModal: React.FC<ChinaShipmentsListModalProps> = ({
  visible,
  onClose,
  order,
}) => {
  const dispatch = useDispatch<any>();
  const { orderDetailShipments, loadingShipmentsAction } = useSelector(
    (state: any) => state.chinaTracker
  );
  const toast = useToast();

  useEffect(() => {
    if (order) {
      dispatch(
        getAllChinaTrackerDetailsWithShipments({
          orderNumber: order.OrderNumber1,
          key: order.Key,
        })
      )
        .unwrap()
        .catch((err: any) => {
          toast.show(err, {
            type: "danger",
          });
        });
    }
  }, [order]);

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <ScrollView contentContainerStyle={styles.scrollContent}>
            {/* Header */}
            <View style={styles.header}>
              <TouchableOpacity onPress={onClose} style={styles.backButton}>
                <Text style={styles.backArrow}>←</Text>
              </TouchableOpacity>
              <Text style={styles.title}>Order #{order?.OrderNumber1}</Text>
            </View>

            {loadingShipmentsAction ? (
              <Spinner
                size="small"
                color="#007AFF"
                styles={{ marginTop: 50, marginBottom: 50 }}
              />
            ) : (
              <>
                {/* Order Information */}
                <View style={styles.sectionCard}>
                  <Text style={styles.sectionTitle}>Order Details</Text>
                  <DetailRow label="Order Number" value={order?.OrderNumber1} />
                  <DetailRow label="Key" value={order?.Key} />
                  <DetailRow
                    label="Priority"
                    value={order?.PriorityOrder || "-"}
                  />
                </View>

                {/* Run Orders */}
                {orderDetailShipments && orderDetailShipments.length > 0 ? (
                  orderDetailShipments.map((detail: any) => (
                    <View key={detail._id} style={styles.runCard}>
                      <Text style={styles.runHeader}>
                        🏭 Run Order #{detail.runOrder}
                      </Text>
                      <View style={styles.runDetails}>
                        <DetailRow
                          label="Planned Qty"
                          value={detail.plannedQty}
                        />
                        <DetailRow
                          label="Production Line"
                          value={detail.allocateProductionLine}
                        />
                        <DetailRow
                          label="Plan Start"
                          value={formatLocalDate(detail.planStartDay)}
                        />
                        <DetailRow
                          label="Est. Completion"
                          value={formatLocalDate(detail.ESTCompletionDate)}
                        />
                        <DetailRow
                          label="ETD"
                          value={formatLocalDate(detail.ETD)}
                        />
                        <DetailRow
                          label="ETA"
                          value={formatLocalDate(detail.ETA)}
                        />
                        <DetailRow
                          label="Expected Arrival"
                          value={formatLocalDate(detail.ExpectedArrival)}
                        />
                      </View>

                      {/* Shipments */}
                      {detail.shipments && detail.shipments.length > 0 ? (
                        <View style={styles.shipmentSection}>
                          <Text style={styles.shipmentTitle}>
                            🚢 Shipments ({detail.shipments.length})
                          </Text>
                          <FlatList
                            data={detail.shipments}
                            keyExtractor={(item) => item._id}
                            scrollEnabled={false}
                            renderItem={({ item }) => (
                              <View style={styles.shipmentCard}>
                                <DetailRow
                                  label="Shipment No"
                                  value={item.shipmentNo}
                                />
                                <DetailRow
                                  label="Qty"
                                  value={item.shipmentQty}
                                />
                                <DetailRow
                                  label="Container"
                                  value={item.containerNo}
                                />
                                <DetailRow
                                  label="ETD"
                                  value={formatLocalDate(item.ETD)}
                                />
                                <DetailRow
                                  label="ETA"
                                  value={formatLocalDate(item.ETA)}
                                />
                              </View>
                            )}
                          />
                        </View>
                      ) : (
                        <Text style={styles.noShipments}>
                          No shipments for this run.
                        </Text>
                      )}
                    </View>
                  ))
                ) : (
                  <Text style={styles.noData}>No factory order details.</Text>
                )}
              </>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const DetailRow = ({ label, value }: { label: string; value?: any }) => (
  <View style={styles.detailRow}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value ?? "-"}</Text>
  </View>
);

const { width, height } = Dimensions.get("window");

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 15,
  },
  modalContainer: {
    backgroundColor: "#fff",
    width: "90%",
    maxHeight: height * 0.85,
    borderRadius: 16,
    padding: 16,
    shadowColor: "#000",
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 5,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 30,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  backButton: {
    marginRight: 8,
  },
  backArrow: {
    fontSize: 26,
    fontWeight: "bold",
    color: "#007AFF",
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "#222",
  },
  sectionCard: {
    backgroundColor: "#f9f9f9",
    borderRadius: 10,
    padding: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#eee",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#444",
    marginBottom: 8,
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
  },
  label: {
    fontSize: 14,
    color: "#555",
    fontWeight: "600",
  },
  value: {
    fontSize: 14,
    color: "#111",
    flexShrink: 1,
    textAlign: "right",
  },
  runCard: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 14,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: "#e1e1e1",
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  runHeader: {
    fontWeight: "700",
    fontSize: 16,
    color: "#007AFF",
    marginBottom: 10,
  },
  runDetails: {
    marginBottom: 8,
  },
  shipmentSection: {
    marginTop: 10,
  },
  shipmentTitle: {
    fontWeight: "700",
    fontSize: 15,
    color: "#333",
    marginBottom: 6,
  },
  shipmentCard: {
    backgroundColor: "#fdfdfd",
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#ddd",
  },
  noShipments: {
    fontSize: 13,
    color: "#999",
    fontStyle: "italic",
  },
  noData: {
    textAlign: "center",
    marginTop: 20,
    color: "#aaa",
    fontStyle: "italic",
  },
});

export default ChinaShipmentsListModal;
